import { memo, useCallback, useMemo, type FC, type MutableRefObject } from "react"
import { CircleMarker, Polyline } from "react-leaflet"
import { useTranslation } from "react-i18next"
import { Box, useTheme } from "@mui/material"
import type {
  CircleMarker as LeafletCircleMarker,
  Polyline as LeafletPolyline,
} from "leaflet"

import type { PowerlineSpanFeature } from "types/geolocation.types"
import type { LastLimiterBySpanId, PeakSpanMarker } from "types/spans.types"

import {
  calculateLimitingSpanIconPosition,
  formatLineStringCoordinates,
  getLineMidpoint,
  WARNING_ICON,
} from "helpers/utils/map"
import {
  POLYLINE_DEFAULT_OPACITY,
  POLYLINE_DEFAULT_WEIGHT,
  POLYLINE_HOVERED_WEIGHT,
  POLYLINE_NOT_HOVERED_OPACITY,
  TOOLTIP_THRESHOLD,
} from "helpers/utils/constants"
import GeoPopup from "widgets/plots/geo/GeoPopup"
import MapMarker from "widgets/plots/geo/MapMarker"
import SeverityDot from "widgets/plots/SeverityDot"
import { MarkerTooltip } from "widgets/styled/map"
import { useZoom } from "./ZoomContext"

// Innermost ring, and the step between stacked ones.
const PEAK_RING_RADIUS = 8
const PEAK_RING_GAP = 3

// The registries the map keeps so it can lift a span or open its popup from
// outside. Refs, so handing them down costs no re-render.
type SpanLayerRegistry = MutableRefObject<Map<number, LeafletPolyline>>

interface SpanLayerProps {
  feature: PowerlineSpanFeature
  // Hover and dimming are resolved by the map, which is the one that knows
  // about the other spans. Booleans, so an unrelated hover leaves them equal.
  isHovered: boolean
  isDimmed: boolean
  neutralRoute: boolean
  // This span's last-limiter telemetry, absent when it never was one.
  limitingTelemetry: LastLimiterBySpanId[number] | undefined
  // The methods this span tops, drawn as stacked rings.
  peakMarkers: PeakSpanMarker[] | undefined
  showPopups: boolean
  // The line's spans in order, which names this span when it has no name.
  lineSpanIds: number[] | undefined
  spanLayerRefs: SpanLayerRegistry
  spanHitLayerRefs: SpanLayerRegistry
  onHoverSpan: (spanId: number) => void
  onHoverOut: () => void
  onSelectSpan?: (spanId: number | null) => void
  onSelectedSpanChange?: (spanId: number | null) => void
}

/**
 * One span of the route: its line, its hit area, the alert icon when it is the
 * last limiting one, and the rings for the methods it tops.
 *
 * Split out and memoized because hovering a ribbon cell lifts the hovered span
 * into the card, which re-renders the map. Only two spans change state on a
 * hover step, so every other one bails out here instead of rebuilding its
 * polylines, its popup and its rings.
 */
const SpanLayer: FC<SpanLayerProps> = ({
  feature,
  isHovered,
  isDimmed,
  neutralRoute,
  limitingTelemetry,
  peakMarkers,
  showPopups,
  lineSpanIds,
  spanLayerRefs,
  spanHitLayerRefs,
  onHoverSpan,
  onHoverOut,
  onSelectSpan,
  onSelectedSpanChange,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const zoom = useZoom()

  const spanId = feature.properties.id
  const coordinates = useMemo(
    () => formatLineStringCoordinates(feature.geometry.coordinates),
    [feature],
  )

  const isLimiting = !neutralRoute && !!limitingTelemetry

  const color = isHovered
    ? theme.palette.primary.dark
    : neutralRoute
      ? theme.palette.spanFrequency.route
      : isLimiting
        ? theme.palette.warning.main
        : theme.palette.primary.dark

  const weight = isHovered ? POLYLINE_HOVERED_WEIGHT : POLYLINE_DEFAULT_WEIGHT

  // Dim non-hovered spans while a span is hovered.
  const opacity = isDimmed ? POLYLINE_NOT_HOVERED_OPACITY : POLYLINE_DEFAULT_OPACITY

  // Currently-limiting spans get the alert icon (+ span popup showing which
  // method(s) are limiting), in both neutral and binary mode.
  const alertPosition = useMemo(
    () => (limitingTelemetry ? calculateLimitingSpanIconPosition(coordinates) : null),
    [limitingTelemetry, coordinates],
  )

  // Halfway along the span, not its middle vertex — for a two-point span that
  // vertex is one of the towers.
  const ringPosition = useMemo(
    () => (peakMarkers?.length ? getLineMidpoint(coordinates) : null),
    [peakMarkers, coordinates],
  )

  // Built once per span rather than per render: a fresh object here would reach
  // every popup as a new prop and undo the memo around this component.
  const spanPopupMetadata = useMemo(
    () => ({ span: { ...limitingTelemetry, peaks: peakMarkers, lineSpanIds } }),
    [limitingTelemetry, peakMarkers, lineSpanIds],
  )

  const registerLine = useCallback(
    (layer: LeafletPolyline | null) => {
      if (layer) spanLayerRefs.current.set(spanId, layer)
      else spanLayerRefs.current.delete(spanId)
    },
    [spanLayerRefs, spanId],
  )

  const registerHitLine = useCallback(
    (layer: LeafletPolyline | null) => {
      if (layer) spanHitLayerRefs.current.set(spanId, layer)
      else spanHitLayerRefs.current.delete(spanId)
    },
    [spanHitLayerRefs, spanId],
  )

  const lineEventHandlers = useMemo(
    () => ({
      mouseover: () => onHoverSpan(spanId),
      mouseout: () => onHoverOut(),
      click: () => onSelectSpan?.(spanId),
      popupopen: () => onSelectedSpanChange?.(spanId),
      popupclose: () => onSelectedSpanChange?.(null),
    }),
    [spanId, onHoverSpan, onHoverOut, onSelectSpan, onSelectedSpanChange],
  )

  const ringEventHandlers = useMemo(
    () => ({
      mouseover: () => onHoverSpan(spanId),
      mouseout: () => onHoverOut(),
      click: () => onSelectSpan?.(spanId),
      popupopen: () => onSelectedSpanChange?.(spanId),
      // Leaflet drops a pinned tooltip while a popup is open on the same
      // layer, so put the label back afterwards.
      popupclose: (event: { target: LeafletCircleMarker }) => {
        onSelectedSpanChange?.(null)
        if (zoom >= TOOLTIP_THRESHOLD) event.target.openTooltip()
      },
    }),
    [spanId, zoom, onHoverSpan, onHoverOut, onSelectSpan, onSelectedSpanChange],
  )

  return (
    <>
      {/* Non-interactive: the wide invisible line below takes the hover and the
          click, since a 4px line is hard to hit. */}
      <Polyline
        ref={registerLine}
        pathOptions={{ color, weight, opacity, interactive: false }}
        positions={coordinates}
      />
      {/* The wide hit area. */}
      <Polyline
        ref={registerHitLine}
        pathOptions={{ weight: 20, opacity: 0 }}
        positions={coordinates}
        eventHandlers={lineEventHandlers}
      >
        {showPopups && (
          <GeoPopup type="span" data={feature.properties} metadata={spanPopupMetadata} />
        )}
      </Polyline>
      {alertPosition && (
        <MapMarker
          position={alertPosition}
          icon={WARNING_ICON}
          zIndexOffset={1000}
          tooltipContent={neutralRoute ? t("spans.ampacity.map.lastLimiting") : undefined}
          // The icon sits off the line, so it takes the click instead of
          // the hit area below. Without these the span you clicked never
          // becomes the selected one.
          eventHandlers={lineEventHandlers}
          popup={
            showPopups ? (
              <GeoPopup
                type="span"
                data={feature.properties}
                metadata={spanPopupMetadata}
              />
            ) : undefined
          }
        />
      )}
      {ringPosition && !!peakMarkers?.length && (
        <>
          {/* Decoration only: the transparent disc below is the one hit target
              for all of this span's rings. */}
          {peakMarkers.map((marker, i) => (
            <CircleMarker
              key={`peak-ring-${marker.dataKey}`}
              center={ringPosition}
              radius={PEAK_RING_RADIUS + i * PEAK_RING_GAP}
              pathOptions={{
                color: marker.color,
                weight: 3,
                fill: false,
                interactive: false,
              }}
            />
          ))}
          <CircleMarker
            center={ringPosition}
            radius={PEAK_RING_RADIUS + (peakMarkers.length - 1) * PEAK_RING_GAP + 4}
            // A zero-opacity fill still takes pointer events.
            pathOptions={{ weight: 0, opacity: 0, fillOpacity: 0 }}
            eventHandlers={ringEventHandlers}
          >
            {/* Pinned only once zoomed in, since the labels crowd the line
                otherwise. `MarkerTooltip` carries the same styling as the tower
                and device labels, and `interactive` lets a click on the label
                reach the marker. */}
            <MarkerTooltip
              key={`peak-label-${zoom >= TOOLTIP_THRESHOLD}`}
              direction="top"
              offset={[0, -4]}
              opacity={1}
              interactive
              permanent={zoom >= TOOLTIP_THRESHOLD}
              sx={{
                cursor: "pointer",
                // Leaflet's own arrow sits detached from the box, so draw one
                // on the box: a square turned 45°.
                "&:before": { display: "none" },
                "&:after": {
                  content: '""',
                  position: "absolute",
                  left: "50%",
                  bottom: 0,
                  width: 9,
                  height: 9,
                  backgroundColor: theme.palette.background.paper,
                  transform: "translate(-50%, 50%) rotate(45deg)",
                },
              }}
            >
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
              >
                {peakMarkers.map((marker) => (
                  <SeverityDot key={marker.dataKey} color={marker.color} />
                ))}
                {/* One span can top both methods. Say it once rather than
                    stacking two labels on the same point. */}
                {peakMarkers.length > 1
                  ? t("spans.ampacity.map.peakBoth")
                  : peakMarkers[0].label}
              </Box>
            </MarkerTooltip>
            {showPopups && (
              <GeoPopup
                type="span"
                data={feature.properties}
                metadata={spanPopupMetadata}
              />
            )}
          </CircleMarker>
        </>
      )}
    </>
  )
}

export default memo(SpanLayer)
